import React, { useState, useEffect, ReactNode } from "react";
import { HStack, Box, Text, Spinner } from "@chakra-ui/react";
import { Input } from "components/RFQ/Input";
import { Button } from "newui/components/Button";
import {
  type Clarification,
  type ClarificationType,
  ClarificationImpact,
  ClarificationStatus,
  type CreateClarificationPayload,
  type UpdateClarificationPayload,
  type SubmitClarificationResponsePayload,
} from "../../types/clarification.type";
import {
  CLARIFICATION_IMPACT_LABELS,
} from "../../constants/clarification.constants";
import { FiEdit2, FiFileText, FiMessageCircle, FiPlusCircle } from "react-icons/fi";
import { ClarificationSupplierResponse } from "./components/ClarificationSupplierResponse";
import { FileableFile, UploadFile } from "types/storage.types";
import { Header } from "./components/Header";
import { ClientCreation } from "./components/ClientCreation";
import { ViewSupplierResponse } from "./components/ViewSupplierResponse";
import type { ModalMode } from "./ClarificationModal";
import { useFileUploadContext } from "newui/components/FileUpload/hooks/FileUploadContext";

export interface ClarificationModalContentProps {
  clarification?: Clarification | null;
  clarificationTypes: ClarificationType[];
  footerExtra?: ReactNode;
  isClientView: boolean;
  supplierName?: string;
  onClientSave: (
    payload: CreateClarificationPayload | UpdateClarificationPayload,
    clarificationId?: number
  ) => Promise<boolean>;
  onClose: (files?: FileableFile[]) => void;
  onSupplierSave: (
    payload: SubmitClarificationResponsePayload,
    clarificationId?: number
  ) => Promise<boolean>;
}

export const getModalMode = (
  clarification: Clarification | null | undefined,
  isClientView: boolean
): ModalMode => {
  if (!clarification) {
    return "create";
  }
  if (isClientView) {
    if (clarification.is_sent || clarification.final_response) {
      return "view";
    }
    return "edit-full";
  }
  if (!isClientView && !clarification?.is_draft_response) {
    return "view";
  }
  if (clarification.status === ClarificationStatus.closed) {
    return "view";
  }
  return "respond";
};

export const getModalHeaderConfig = (
  mode: ModalMode,
  supplierName?: string,
  clarification?: Clarification | null
): { bgColor: string; description: string; icon: ReactNode; iconColor: string; title: string } => {
  const headers: Record<ModalMode, { bgColor: string; description: string; icon: ReactNode; iconColor: string; title: string }> = {
    create: {
      icon: <FiPlusCircle className="stroke-blue-400" size={40} />,
      bgColor: "bg-brand-secondary",
      iconColor: "text-utility-brand-700",
      title: `Create Clarification for ${supplierName}`,
      description: "Request additional information from the supplier.",
    },
    view: {
      icon: <FiFileText className="stroke-blue-400" size={40} />,
      bgColor: "bg-gray-100",
      iconColor: "text-gray-700",
      title: `Clarification for ${clarification?.supplier_name}`,
      description: "Review the clarification details and response.",
    },
    respond: {
      icon: <FiMessageCircle className="stroke-blue-400" size={40} />,
      bgColor: "bg-brand-secondary",
      iconColor: "text-utility-brand-700",
      title: "Respond to Clarification",
      description: "Draft your response to this clarification request.",
    },
    edit: {
      icon: <FiEdit2 className="stroke-blue-400" size={30} />,
      bgColor: "bg-brand-secondary",
      iconColor: "text-utility-brand-700",
      title: `Update Clarification for ${clarification?.supplier_name}`,
      description: "Update the status or impact of this clarification.",
    },
    "edit-full": {
      icon: <FiEdit2 className="stroke-blue-400" size={30} />,
      bgColor: "bg-brand-secondary",
      iconColor: "text-utility-brand-700",
      title: `Edit Clarification for ${clarification?.supplier_name}`,
      description: "Edit the clarification details before a response is received.",
    },
  };
  return headers[mode];
};

export const ClarificationModalContent: React.FC<ClarificationModalContentProps> = ({
  clarification,
  clarificationTypes,
  isClientView,
  supplierName,
  onClientSave,
  onClose,
  onSupplierSave,
  footerExtra,
}) => {
  const { isSafeToClose } = useFileUploadContext();
  const [uploadFiles, setUploadFiles] = useState<FileableFile[]>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    clarification_text: "",
    clarification_type_id: 0,
    relates_to: "",
    owner: "",
    clarification_impact_id: null as ClarificationImpact,
    status: null as ClarificationStatus,
    final_response: "",
    files: [] as UploadFile[],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const mode = getModalMode(clarification, isClientView);
  const header = getModalHeaderConfig(mode, supplierName, clarification);

  const handleModalClosure = () => {
    if (!isSafeToClose()) {
      return;
    }
    onClose(uploadFiles);
  };

  useEffect(() => {
    if (clarification) {
      setFormData({
        clarification_text: clarification.clarification_text || "",
        clarification_type_id: clarification.clarification_type_id || 0,
        relates_to: clarification.relates_to || "",
        owner: clarification.owner || "",
        clarification_impact_id: clarification.clarification_impact_id || null,
        status: clarification.status || null,
        final_response: clarification.final_response || "",
        files: clarification.files || [],
      });
    } else {
      setFormData({
        clarification_text: "",
        clarification_type_id: clarificationTypes[0]?.id || 0,
        relates_to: "",
        owner: "",
        clarification_impact_id: null,
        status: null,
        final_response: "",
        files: [],
      });
    }
    setErrors({});
  }, [clarification, clarificationTypes]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (mode === "create" || mode === "edit-full") {
      if (!formData.clarification_text.trim()) {
        newErrors.clarification_text = "Clarification text is required";
      }
      if (!formData.clarification_type_id) {
        newErrors.clarification_type_id = "Type is required";
      }
      if (!formData.owner.trim()) {
        newErrors.owner = "Owner is required";
      }
    }

    if (mode === "respond" && !formData.final_response.trim()) {
      newErrors.final_response = "Response is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      if (mode === "respond") {
        const responsePayload = {
          final_response: formData.final_response,
          clarification_impact_id: (formData.clarification_impact_id as ClarificationImpact) || undefined,
        } as SubmitClarificationResponsePayload;

        const result = await onSupplierSave(responsePayload, clarification?.id);
        if (result) {
          onClose();
        }
        return;
      }

      let payload: CreateClarificationPayload | UpdateClarificationPayload;

      if (mode === "create") {
        payload = {
          clarification_text: formData.clarification_text,
          clarification_type_id: formData.clarification_type_id,
          relates_to: formData.relates_to || undefined,
          owner: formData.owner,
        };
      } else if (mode === "edit-full") {
        payload = {
          clarification_text: formData.clarification_text,
          clarification_type_id: formData.clarification_type_id,
          relates_to: formData.relates_to || undefined,
          owner: formData.owner,
        };
      } else {
        payload = {
          status: (formData.status as ClarificationStatus) || undefined,
        };
      }

      const success = await onClientSave(payload, clarification?.id);
      if (success) {
        onClose();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | number | FileableFile[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }

    if (field === "files") {
      setUploadFiles(value as FileableFile[]);
    }
  };

  const isViewOnly = mode === "view";

  const impactOptions = Object.entries(CLARIFICATION_IMPACT_LABELS).map(
    ([value, label]) => ({
      value: Number(value) as ClarificationImpact,
      label,
    })
  );

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b border-gray-100 px-6 pb-4 pt-6">
        <HStack align="left" spacing={5} className="flex items-center">
          <Box borderRadius="full">
            {header.icon}
          </Box>
          <div className="flex flex-col gap-0 text-left">
            <Text fontSize="2xl" fontWeight="bold" color="gray.800">
              {header.title}
            </Text>
            <Text fontSize="md" fontWeight="normal">
              {header.description}
            </Text>
          </div>
        </HStack>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="space-y-4">
          {clarification && <Header clarification={clarification} mode={mode} />}

          {(mode === "create" || mode === "edit-full") && (
            <ClientCreation
              clarificationTypes={clarificationTypes}
              errors={errors}
              formData={formData}
              handleInputChange={handleInputChange}
              impactOptions={impactOptions}
            />
          )}

          {(mode === "view" || mode === "respond" || mode === "edit") && (
            <div className="rounded-lg bg-gray-50 p-4 gap-6 flex flex-col">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Clarification Request
                </label>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                  {clarification?.clarification_text}
                </p>
              </div>
              {clarification?.relates_to && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Relates To
                  </label>
                  <p className="text-sm text-gray-900">{clarification?.relates_to || "-"}</p>
                </div>
              )}
            </div>
          )}

          {mode === "respond" && (
            <ClarificationSupplierResponse
              clarificationId={clarification.id}
              errors={errors}
              formData={formData}
              handleInputChange={handleInputChange}
              initialFiles={clarification.files}
              isViewOnly={isViewOnly}
            />
          )}

          {mode === "view" && clarification?.final_response && (
            <ViewSupplierResponse clarification={clarification} />
          )}

          {mode === "edit" && (
            <>
              {clarification?.final_response && (
                <div className="rounded-lg bg-blue-50 p-4">
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Supplier Response
                  </label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">
                    {clarification.final_response}
                  </p>
                </div>
              )}
              {/* @ts-ignore */}
              <Input
                label="Impact"
                name="impact"
                type="select"
                options={impactOptions}
                value={formData.clarification_impact_id}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  handleInputChange("impact", e.target.value)
                }
              />
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100 px-6 py-4">
        <div className="flex w-full items-center gap-2">
          <div className="flex flex-1 justify-between gap-2">
            {isViewOnly ? (
              <div className="flex w-full justify-end">
                <Button onClick={handleModalClosure}>Close</Button>
              </div>
            ) : (
              <>
                <Button onClick={handleModalClosure} btntype="base" className="!w-full" disabled={isLoading}>
                  Cancel
                </Button>
                <Button
                  className="flex !w-full gap-3"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  data-testid="clarification-submit-button"
                >
                  {isLoading ? (
                    <Spinner size="sm" />
                  ) : mode === "create" ? (
                    "Create"
                  ) : mode === "edit-full" ? (
                    "Save Changes"
                  ) : mode === "respond" ? (
                    "Save Draft"
                  ) : (
                    "Save"
                  )}
                </Button>
              </>
            )}
          </div>
          {footerExtra}
        </div>
      </div>
    </div>
  );
};
